import { zodResolver } from '@hookform/resolvers/zod';
import { Group, Stack } from '@mantine/core';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { CourseMembershipSelectControl } from '@/module/Nomen/CourseMembership/CourseMembershipSelectControl';
import { PublisherSelectControl } from '@/module/Nomen/Publisher/PublisherSelectControl';

import { AuthorListControl, Content, Form, FormSubmit, NumberInputControl, TextInputControl } from '@/core/components';

import { CourseSchema } from './model/CourseSchema';

interface CourseFormProps extends FormSubmit<CourseSchema> {
  title: string;
}

export function CourseForm(props: CourseFormProps) {
  const { title, schema, defaultValues, values, isSubmitting, onCancel, onSubmit } = props;
  const { t } = useTranslation();
  const form = useForm<CourseSchema>({
    defaultValues,
    values,
    resolver: zodResolver(schema),
  });

  return (
    <FormProvider {...form}>
      <Form
        title={title}
        isSubmitting={isSubmitting}
        onCancel={onCancel}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <Stack>
          <Content type="full">
            <TextInputControl<CourseSchema>
              name="title"
              label={t('courses.form.fields.title')}
              isSubmitting={isSubmitting}
            />
            <Group align="flex-end">
              <PublisherSelectControl<CourseSchema>
                name="publisher"
                label={t('courses.form.fields.publisher')}
                isSubmitting={isSubmitting}
              />
              <NumberInputControl<CourseSchema>
                name="noOfPages"
                label={t('courses.form.fields.noOfPages')}
                isSubmitting={isSubmitting}
                allowDecimal={false}
                flex={0.4}
              />
              <NumberInputControl<CourseSchema>
                name="year"
                label={t('courses.form.fields.year')}
                isSubmitting={isSubmitting}
                allowDecimal={false}
                flex={0.4}
              />
            </Group>
          </Content>
          <Content type="full">
            <AuthorListControl<CourseSchema>
              name="authors"
              isSubmitting={isSubmitting}
              label={t('authors.form.title')}
              renderMembership={({ membershipName }) => (
                <CourseMembershipSelectControl
                  label={t('authors.form.fields.membership')}
                  name={membershipName}
                  isSubmitting={isSubmitting}
                  isDisabled={isSubmitting}
                />
              )}
            />
          </Content>
        </Stack>
      </Form>
    </FormProvider>
  );
}
